import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { DateRangePicker } from 'react-dates';
import { Button, Dropdown } from 'semantic-ui-react';
import MultiSelect from '../multiSelect';
import {
  doGetLocationDataView,
  openDataViewLocations,
} from '../../actions/data-view-actions.js';
import { formatCentAmount, formatDate } from '../../lib/formatters.js';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import 'semantic-ui-css/components/button.min.css';
import 'semantic-ui-css/components/dropdown.min.css';
import './fulfilled-spend-section.css';

const VIEW = 'customer_reqs_fulfilled_dates';
const TOPIC_MODEL = 'location';

export class FulfilledSpendSection extends React.PureComponent {
  constructor(props) {
    super(props);
    const startDate = moment().startOf('month');
    const endDate = moment().endOf('month');
    this.state = {
      startDate,
      endDate,
      focusedInput: null,
      fetchedStart: null,
      fetchedEnd: null,
      fetchedLocationIds: null,
    };
  }

  componentDidMount() {
    this._fetchData();
  }

  _fetchData = () => {
    const { startDate, endDate } = this.state;
    const start = new Date(startDate.startOf('day')).toJSON();
    const end = new Date(endDate.endOf('day')).toJSON();
    this.props.viewingLocations.forEach((location) => {
      this.props.actions.doGetLocationDataView({
        location_id: location.id,
        view: VIEW,
        start,
        end,
      });
    });
    this.setState({
      fetchedStart: start,
      fetchedEnd: end,
      fetchedLocationIds: this.props.viewingLocations.map((l) => l.id).join(','),
    });
  };

  _isApplyDisabled = () => {
    const { fetchedStart, fetchedEnd, fetchedLocationIds } = this.state;
    if (!fetchedStart) return false;
    const start = new Date(this.state.startDate.startOf('day')).toJSON();
    const end = new Date(this.state.endDate.endOf('day')).toJSON();
    return (
      fetchedStart === start &&
      fetchedEnd === end &&
      fetchedLocationIds === this.props.viewingLocations.map((l) => l.id).join(',')
    );
  };

  _getFulfilledData = () => {
    const { fetchedStart, fetchedEnd, fetchedLocationIds } = this.state;
    if (!fetchedStart || !fetchedEnd || !fetchedLocationIds) return [];
    const ids = fetchedLocationIds.split(',').map(Number);
    return this.props.allFulfilledData.filter(
      (d) =>
        d.start === fetchedStart &&
        d.end === fetchedEnd &&
        ids.indexOf(d.topic.id) > -1
    );
  };

  _totalFulfilledSpend = () => {
    return this._getFulfilledData().reduce((sum, series) => {
      return series.data.reduce((s, item) => s + (item.total_price || 0), sum);
    }, 0);
  };

  _presets = () => {
    return [
      {
        text: 'Last Year',
        key: 'last_year',
        startDate: moment().startOf('year').subtract(1, 'week').startOf('year'),
        endDate: moment().startOf('year').subtract(1, 'day'),
      },
      {
        text: 'Last Quarter',
        key: 'last_quarter',
        startDate: moment().startOf('quarter').subtract(1, 'week').startOf('quarter'),
        endDate: moment().startOf('quarter').subtract(1, 'day'),
      },
      {
        text: 'Last Month',
        key: 'last_month',
        startDate: moment().startOf('month').subtract(1, 'week').startOf('month'),
        endDate: moment().startOf('month').subtract(1, 'day'),
      },
      {
        text: 'Current Month',
        key: 'current_month',
        startDate: moment().startOf('month'),
        endDate: moment().endOf('month'),
      },
    ].map((opt) => ({
      ...opt,
      value: `${opt.startDate.format('YYYY-MM-DD')}-${opt.endDate.format('YYYY-MM-DD')}`,
    }));
  };

  _activePreset = () => {
    return this._presets().find(
      (p) =>
        p.startDate.isSame(this.state.startDate, 'day') &&
        p.endDate.isSame(this.state.endDate, 'day')
    );
  };

  handlePresetChange = (e, { value }) => {
    const preset = this._presets().find((p) => p.value === value);
    if (preset) {
      this.setState({ startDate: preset.startDate, endDate: preset.endDate });
    }
  };

  handleDatesChange = ({ startDate, endDate }) => {
    this.setState({
      startDate: startDate || this.state.startDate,
      endDate: endDate || this.state.endDate,
    });
  };

  _onLocationSelect = (selected) => {
    const locations = this.props.allLocations.filter(
      (loc) => selected.indexOf(loc.id) > -1
    );
    if (!locations.length) return;
    this.props.actions.openDataViewLocations(locations);
  };

  _locationValueRenderer = (selected, options) => {
    if (selected.length === 0) return 'Select a Location...';
    if (selected.length === options.length) return 'Viewing All Locations';
    if (selected.length === 1) return;
    return `Viewing ${selected.length} Locations`;
  };

  _renderTable = () => {
    const rows = this._getFulfilledData().flatMap((series) => series.data);
    if (!rows.length) return null;
    return (
      <table className="fulfilled-spend-table">
        <thead>
          <tr>
            <th>Order #</th>
            <th>Fulfilled Date</th>
            <th>Total Spend</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => (
            <tr key={item.id}>
              <td>{item.id}</td>
              <td>{formatDate(item.fulfilled_at)}</td>
              <td><strong>{formatCentAmount(item.total_price || 0)}</strong></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  render() {
    const total = this._totalFulfilledSpend();
    const active = this._activePreset();
    const locationOptions = this.props.allLocations.map((l) => ({ value: l.id, label: l.name }));
    const selectedLocationIds = this.props.viewingLocations.map((l) => l.id);

    return (
      <div className="fulfilled-spend-section">
        <hr className="fulfilled-spend-divider" />
        <div className="fulfilled-spend-header-row">
          <h2 className="fulfilled-spend-heading">Fulfilled Spend</h2>
          <div className="fulfilled-spend-controls">
            {this.props.allLocations.length > 1 && (
              <div className="fulfilled-spend-location-select">
                <MultiSelect
                  valueRenderer={this._locationValueRenderer}
                  selected={selectedLocationIds}
                  onSelectedChanged={this._onLocationSelect}
                  selectAllLabel="All Locations"
                  options={locationOptions}
                />
              </div>
            )}
            <DateRangePicker
              startDateId="fulfilled-start"
              endDateId="fulfilled-end"
              startDate={this.state.startDate}
              endDate={this.state.endDate}
              onDatesChange={this.handleDatesChange}
              focusedInput={this.state.focusedInput}
              onFocusChange={(focusedInput) => this.setState({ focusedInput })}
              numberOfMonths={2}
              isOutsideRange={() => false}
            />
            <Dropdown
              selection
              text={(active || { text: 'Custom Range' }).text}
              value={(active || {}).value}
              options={this._presets()}
              onChange={this.handlePresetChange}
              className="fulfilled-spend-preset"
            />
            <Button
              disabled={this._isApplyDisabled()}
              onClick={this._fetchData}
              className="fulfilled-spend-apply">
              Apply
            </Button>
          </div>
        </div>
        <p className="fulfilled-spend-total">
          Total fulfilled/processed spend:{' '}
          <strong>{formatCentAmount(total)}</strong>
        </p>
        {this._renderTable()}
      </div>
    );
  }
}

FulfilledSpendSection.propTypes = {
  viewingLocations: PropTypes.array.isRequired,
  allLocations: PropTypes.array.isRequired,
  allFulfilledData: PropTypes.array.isRequired,
  actions: PropTypes.shape({
    doGetLocationDataView: PropTypes.func.isRequired,
    openDataViewLocations: PropTypes.func.isRequired,
  }).isRequired,
};

function mapStateToProps(state) {
  return {
    viewingLocations: state.dataViews.openLocations.length
      ? state.dataViews.openLocations
      : [state.locations.open],
    allLocations: Object.values(state.locations.items),
    allFulfilledData: state.dataViews.items.filter(
      (d) => d.view === VIEW && d.topic.model === TOPIC_MODEL
    ),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ doGetLocationDataView, openDataViewLocations }, dispatch),
  };
}

function areStatesEqual(prev, next) {
  return (
    prev.dataViews.items === next.dataViews.items &&
    prev.dataViews.openLocations === next.dataViews.openLocations &&
    prev.locations.open === next.locations.open &&
    prev.locations.items === next.locations.items
  );
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  areStatesEqual,
})(FulfilledSpendSection);
